import { useEffect, useState } from "react";
import { toast } from "sonner";
import { api, apiError } from "@/lib/api";
import { Input } from "@/components/ui/input";
import { Switch } from "@/components/ui/switch";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter } from "@/components/ui/dialog";
import { Plus, Loader2 } from "lucide-react";

const empty = { name: "", slug: "", price_inr: 0, tagline: "", features: [], page_length: "",
  sections: [], delivery_delay_minutes: 390, methodology_options: ["pythagorean", "chaldean"],
  languages: ["English"], active: true, featured: false };

export default function Packages() {
  const [packages, setPackages] = useState([]);
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(empty);
  const [saving, setSaving] = useState(false);

  const load = () => api.get("/admin/packages").then((r) => setPackages(r.data)).catch(() => {});
  useEffect(() => { load(); }, []);

  const openNew = () => { setEditing(null); setForm(empty); setOpen(true); };
  const openEdit = (p) => {
    setEditing(p);
    setForm({ ...p, features: p.features || [], sections: p.sections || [] });
    setOpen(true);
  };

  const save = async () => {
    setSaving(true);
    try {
      const payload = {
        ...form,
        price_inr: Number(form.price_inr),
        delivery_delay_minutes: Number(form.delivery_delay_minutes),
        features: Array.isArray(form.features) ? form.features : String(form.features).split("\n").filter(Boolean),
        sections: Array.isArray(form.sections) ? form.sections : String(form.sections).split("\n").filter(Boolean),
      };
      if (editing) await api.put(`/admin/packages/${editing.package_id}`, payload);
      else await api.post("/admin/packages", payload);
      toast.success("Package saved"); setOpen(false); load();
    } catch (e) { toast.error(apiError(e)); }
    finally { setSaving(false); }
  };

  const disable = async (p) => {
    try { await api.delete(`/admin/packages/${p.package_id}`); toast.success("Package disabled"); load(); }
    catch (e) { toast.error(apiError(e)); }
  };

  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  return (
    <div data-testid="admin-packages">
      <div className="flex items-center justify-between mb-6">
        <h1 className="font-serif text-3xl text-navy">Packages</h1>
        <button onClick={openNew} data-testid="new-package" className="btn-primary text-sm"><Plus className="w-4 h-4" /> New package</button>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
        {packages.map((p) => (
          <div key={p.package_id} className="bg-white rounded-xl border border-slate-200 p-5" data-testid={`pkg-${p.slug}`}>
            <div className="flex justify-between items-start mb-2">
              <h3 className="font-serif text-xl text-navy">{p.name}</h3>
              <span className={`text-xs px-2 py-0.5 rounded-full ${p.active ? "bg-emerald-100 text-emerald-700" : "bg-slate-200 text-slate-600"}`}>{p.active ? "Active" : "Disabled"}</span>
            </div>
            <p className="text-2xl font-serif text-gold-hover mb-1">₹{p.price_inr}</p>
            <p className="text-xs text-slate-500 mb-3">{p.features?.length || 0} features · {p.delivery_delay_minutes} min delay</p>
            <div className="flex gap-2">
              <button onClick={() => openEdit(p)} data-testid={`edit-pkg-${p.slug}`} className="text-sm text-gold-hover hover:underline">Edit</button>
              {p.active && <button onClick={() => disable(p)} data-testid={`disable-pkg-${p.slug}`} className="text-sm text-rose-600 hover:underline">Disable</button>}
            </div>
          </div>
        ))}
      </div>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-w-lg max-h-[90vh] overflow-y-auto">
          <DialogHeader><DialogTitle className="font-serif text-2xl">{editing ? "Edit package" : "New package"}</DialogTitle></DialogHeader>
          <div className="space-y-4">
            <L label="Name"><Input data-testid="pkg-name" value={form.name} onChange={set("name")} /></L>
            <L label="Slug (unique)"><Input data-testid="pkg-slug" value={form.slug} onChange={set("slug")} disabled={!!editing} /></L>
            <L label="Price (₹)"><Input data-testid="pkg-price" type="number" value={form.price_inr} onChange={set("price_inr")} /></L>
            <L label="Tagline"><Input value={form.tagline} onChange={set("tagline")} /></L>
            <L label="Page length"><Input value={form.page_length} onChange={set("page_length")} /></L>
            <L label="Delivery delay (minutes)"><Input data-testid="pkg-delay" type="number" value={form.delivery_delay_minutes} onChange={set("delivery_delay_minutes")} /></L>
            <L label="Features (one per line)">
              <textarea className="w-full border border-input rounded-md p-2 text-sm" rows={4}
                value={Array.isArray(form.features) ? form.features.join("\n") : form.features}
                onChange={(e) => setForm({ ...form, features: e.target.value.split("\n") })} />
            </L>
            <L label="Report sections (one per line)">
              <textarea className="w-full border border-input rounded-md p-2 text-sm" rows={4}
                value={Array.isArray(form.sections) ? form.sections.join("\n") : form.sections}
                onChange={(e) => setForm({ ...form, sections: e.target.value.split("\n") })} />
            </L>
            <div className="flex items-center gap-3">
              <Switch data-testid="pkg-featured" checked={form.featured} onCheckedChange={(v) => setForm({ ...form, featured: v })} />
              <span className="text-sm text-slate-700">Featured (highlighted)</span>
            </div>
            <div className="flex items-center gap-3">
              <Switch data-testid="pkg-active" checked={form.active} onCheckedChange={(v) => setForm({ ...form, active: v })} />
              <span className="text-sm text-slate-700">Active</span>
            </div>
          </div>
          <DialogFooter>
            <button onClick={save} disabled={saving} data-testid="save-package" className="btn-primary w-full">
              {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : null} Save package
            </button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}

function L({ label, children }) {
  return <div><label className="text-sm font-medium text-slate-700 mb-1 block">{label}</label>{children}</div>;
}
